import { useQueryClient } from '@tanstack/react-query';
import { useSimplifiedAuth } from '@/contexts/SimplifiedAuthContext';
import { useNavigationHelper } from './useNavigationHelper';

/**
 * Centralized data refresh hook - replaces window.location.reload() with React-based solutions
 */
export function useDataRefresh() {
  const queryClient = useQueryClient();
  const { refetchData } = useSimplifiedAuth();
  const { navigate } = useNavigationHelper();

  const refreshAuthData = async () => {
    try {
      await refetchData();
      console.log('✅ Auth data refreshed successfully');
    } catch (error) {
      console.error('❌ Failed to refresh auth data:', error);
      throw error;
    }
  };

  const refreshQueryData = async (queryKeys?: string[]) => {
    try {
      if (queryKeys) {
        // Invalidate specific queries
        await Promise.all(
          queryKeys.map(key => queryClient.invalidateQueries({ queryKey: [key] }))
        );
      } else {
        // Invalidate all queries
        await queryClient.invalidateQueries();
      }
      console.log('✅ Query data refreshed successfully');
    } catch (error) {
      console.error('❌ Failed to refresh query data:', error);
      throw error;
    }
  };

  const refreshCurrentRoute = () => {
    const currentPath = window.location.pathname;
    navigate(currentPath, 'data-refresh');
  };

  const fullRefresh = async () => {
    try {
      await Promise.all([
        refreshAuthData(),
        refreshQueryData()
      ]);
      console.log('✅ Full data refresh completed');
    } catch (error) {
      console.error('❌ Full refresh failed:', error);
      // As last resort, delegate to safe reload
      console.warn('⚠️ Full refresh failed, using safe reload fallback');
      const { safeReload } = useNavigationHelper();
      await safeReload('full-refresh-fallback');
    }
  };

  return {
    refreshAuthData,
    refreshQueryData,
    refreshCurrentRoute,
    fullRefresh
  };
}